import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  McpAdminIntegrationListItem,
  McpDiscoveryResult,
  McpIntegrationImpact,
  McpIntegrationTestResult,
  McpOauthCallbackInfo,
  McpOauthDiscoveryView,
} from "@yazamco/shared-types";
import type {
  McpIntegrationCreateInput,
  McpIntegrationUpdateInput,
} from "@yazamco/validation";
import {
  createMcpIntegration,
  deleteMcpIntegration,
  discoverMcpOauth,
  discoverMcpTools,
  fetchMcpAdminIntegrations,
  fetchMcpOauthCallbackUrl,
  setMcpAllowedTools,
  testMcpIntegration,
  updateMcpIntegration,
} from "../../lib/copilot-mcp-admin";

/**
 * Query state for the Business Integrations admin screen.
 *
 * Two things about the invalidation policy are deliberate.
 *
 * FIRST: every mutation invalidates the admin list, because every one of them
 * changes something the list renders — the enabled badge, the tool count, the
 * "Configured ✓" state, the delete impact. Invalidating a narrower key would
 * mean picking, per mutation, which of those had moved, and getting one wrong
 * shows an administrator a screen that disagrees with what they just did.
 *
 * SECOND: they also invalidate the USER-FACING integration list
 * (`["copilot", "mcpIntegrations"]`). An administrator is also a Copilot user,
 * and the two lists describe the same objects from different sides: enabling
 * Monday here must make it appear in their own Business tools panel without a
 * reload. The key is written out rather than imported from the Copilot feature
 * so that this module does not depend on it — the coupling is one string, and
 * a stale key costs a refresh rather than correctness.
 */
export const mcpAdminKeys = {
  integrations: ["settings", "mcpIntegrations"] as const,
  oauthCallback: ["settings", "mcpOauthCallback"] as const,
};

/** The user-facing list, invalidated alongside every admin write. See above. */
const USER_INTEGRATIONS_KEY = ["copilot", "mcpIntegrations"] as const;

export function useMcpAdminIntegrations(
  enabled: boolean,
): UseQueryResult<{ items: McpAdminIntegrationListItem[] }> {
  return useQuery({
    queryKey: mcpAdminKeys.integrations,
    queryFn: fetchMcpAdminIntegrations,
    // Gated on the caller holding the management action. Not a security
    // boundary — the API decides that — but a query fired by somebody who will
    // certainly get a 403 produces an error toast for a screen they cannot see.
    enabled,
    staleTime: 15_000,
  });
}

/**
 * The OAuth callback URL to register with a provider.
 *
 * Long `staleTime`: it is deployment configuration and changes when somebody
 * changes an environment variable, which is not something a wizard needs to
 * poll for.
 */
export function useMcpOauthCallbackUrl(
  enabled: boolean,
): UseQueryResult<McpOauthCallbackInfo> {
  return useQuery({
    queryKey: mcpAdminKeys.oauthCallback,
    queryFn: fetchMcpOauthCallbackUrl,
    enabled,
    staleTime: 5 * 60_000,
  });
}

function useInvalidateAll(): () => void {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: mcpAdminKeys.integrations });
    void client.invalidateQueries({ queryKey: USER_INTEGRATIONS_KEY });
  };
}

export function useCreateMcpIntegration(): UseMutationResult<
  { integration: McpAdminIntegrationListItem },
  Error,
  McpIntegrationCreateInput
> {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: createMcpIntegration,
    onSuccess: invalidate,
  });
}

export function useUpdateMcpIntegration(): UseMutationResult<
  { integration: McpAdminIntegrationListItem },
  Error,
  { id: string; input: McpIntegrationUpdateInput }
> {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, input }) => updateMcpIntegration(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteMcpIntegration(): UseMutationResult<
  { ok: true; impact: McpIntegrationImpact },
  Error,
  { id: string; confirmSlug: string }
> {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, confirmSlug }) => deleteMcpIntegration(id, confirmSlug),
    onSuccess: invalidate,
  });
}

/**
 * Test and discovery do NOT invalidate anything.
 *
 * Both are reads against the far end, and neither changes a row. Invalidating
 * on them would refetch the list every time somebody pressed Test, which is
 * both wasteful and confusing — the screen would flicker as though something
 * had been saved.
 */
export function useTestMcpIntegration(): UseMutationResult<
  { result: McpIntegrationTestResult },
  Error,
  string
> {
  return useMutation({ mutationFn: testMcpIntegration });
}

export function useDiscoverMcpTools(): UseMutationResult<
  McpDiscoveryResult,
  Error,
  string
> {
  return useMutation({ mutationFn: discoverMcpTools });
}

/**
 * Reading a provider's OAuth settings off its endpoint.
 *
 * A mutation and not a query, for the same reason `useTestMcpIntegration` is:
 * it runs when an administrator presses a button, against whatever is currently
 * typed in the endpoint field, and a query keyed on a half-typed URL would fire
 * against every intermediate value somebody types. It invalidates nothing —
 * discovery writes no row.
 */
export function useDiscoverMcpOauth(): UseMutationResult<
  { discovery: McpOauthDiscoveryView },
  Error,
  { endpoint: string; clientName?: string }
> {
  return useMutation({
    mutationFn: ({ endpoint, clientName }) => discoverMcpOauth(endpoint, clientName),
  });
}

export function useSetMcpAllowedTools(): UseMutationResult<
  { integration: McpAdminIntegrationListItem },
  Error,
  { id: string; tools: string[]; version: number }
> {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, tools, version }) => setMcpAllowedTools(id, tools, version),
    onSuccess: invalidate,
  });
}
